import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Link,
  SelectChangeEvent
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CloudUpload, Description } from '@mui/icons-material';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import Swal from 'sweetalert2';
import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'] as const;
const FILE_ACCEPT_ATTRIBUTE = '.pdf,.doc,.docx,.xls,.xlsx';

const REFERENCE_DOCUMENTS = {
  NON_PREMIUM: {
    path: '/assets/brand/NonPremiumEndorsements.docx',
    downloadName: 'NonPremiumEndorsements.docx',
    displayName: 'NonPremiumEndorsements'
  }
} as const;

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  blobAddress?: string;
}

interface State {
  stateId: number;
  stateName: string;
  postalAbbv: string;
}

interface Partner {
    partnersId: number;
    customerName: string;
  }

interface PriorSubmission {
  nonPremiumEndorsementId?: number;
  document?: string;
  client?: string;
  state?: string;
  blobAddress?: string;
  fileSizeKB?: number;
  dateReceived?: string;
  }
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const createFileObject = (file: File): UploadedFile => ({
  id: Math.random().toString(36).substr(2, 9),
  file,
  name: file.name,
  size: file.size,
  blobAddress: URL.createObjectURL(file)
});

const downloadDocument = (path: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = path;
  link.download = fileName;
  link.target = '_blank';
  link.click();
};

const NonPremiumEndorsements: React.FC = () => {
  const { user } = useAuth();
  const token = user?.token;
  
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReferenceDialog, setShowReferenceDialog] = useState(false);
  const [showReferenceSheetDialog, setShowReferenceSheetDialog] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<'new' | 'prior'>('new');
  const [priorSubmissions, setPriorSubmissions] = useState<PriorSubmission[]>([]);
  const [priorSubmissionsLoading, setPriorSubmissionsLoading] = useState(false);
  const [priorSubmissionsPageSize, setPriorSubmissionsPageSize] = useState(10);
  const [priorSubmissionsCurrentPage, setPriorSubmissionsCurrentPage] = useState(1);
  const [priorSubmissionsTotalCount, setPriorSubmissionsTotalCount] = useState(0);


  useEffect(() => {
    if (token) {
      fetchStates();
      getPartnersList();
    }
  }, [token]);

  // Fetch states from API
    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const response = await apiClient('/States/getStates', {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        });

        if (response && response.data) {
          const allowedStates = ['California', 'Nevada', 'Pennsylvania', 'Texas', 'Washington'];
          const filteredStates = response.data.filter((state: State) => 
            allowedStates.includes(state.stateName)
          );
          setStates(filteredStates);
        }
      } catch (error) {
        console.error('Error fetching states:', error);
        setStates([]);
      } finally {
        setStatesLoading(false);
      }
    };


  const getPartnersList = () => {
    apiClient("/customer/getPartnersList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        
        if (responseJson.data && responseJson.data.length > 0) {
          setPartners(responseJson.data);
        } else {
          setPartners([]);
        }
      })
      .catch((error) => {
        setPartners([]);
      });
  };

  useEffect(() => {
    if(partners.length === 1){
      setSelectedPartner(partners[0].customerName);
    }
  }, [partners]);

  const fetchPriorSubmissions = useCallback(async () => {
    if (!token) return;
    
    setPriorSubmissionsLoading(true);
    try {
      const payload = {
        pageSize: priorSubmissionsPageSize,
        pageNumber: priorSubmissionsCurrentPage
      };

      const response = await apiClient('/NonPremiumEndorsement/getPriorSubmissiondocuments', {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      });

      if (response?.data) {
        setPriorSubmissions(response.data);
        setPriorSubmissionsTotalCount(response.totalCount || 0);
      } else {
        setPriorSubmissions([]);
        setPriorSubmissionsTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching prior submissions:', error);
      setPriorSubmissions([]);
      setPriorSubmissionsTotalCount(0);
    } finally {
      setPriorSubmissionsLoading(false);
    }
  }, [token, priorSubmissionsPageSize, priorSubmissionsCurrentPage]);
  useEffect(() => {
    if (selectedTab === 'prior') {
      fetchPriorSubmissions();
    }
  }, [selectedTab, fetchPriorSubmissions]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    const invalidFiles: string[] = [];
    
    // Check file types
    Array.from(files).forEach(file => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(fileExtension as any)) {
        invalidFiles.push(file.name);
      }
    });
    
    // Show error if invalid files found
    if (invalidFiles.length > 0) {
      Swal.fire({
        title: 'Invalid File Type',
        text: `Only PDF, DOC, DOCX, XLS, and XLSX files are allowed. Invalid files: ${invalidFiles.join(', ')}`,
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }
    
    const newFiles = Array.from(files).map(createFileObject);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => {
      const fileToRemove = prev.find(file => file.id === fileId);
      if (fileToRemove?.blobAddress) {
        URL.revokeObjectURL(fileToRemove.blobAddress);
      }
      return prev.filter(file => file.id !== fileId);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };



  const uploadedFilesColumns: GridColDef[] = useMemo(() => [
    {
      field: 'fileName',
      headerName: 'File Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Link 
          href={params.row.blobAddress} 
          target="_blank" 
          sx={{ 
            textDecoration: 'none', 
            overflow: 'initial', 
            whiteSpace: 'break-spaces',
            lineHeight: 'normal',
            color: '#162E3C',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {params.value || '-'}
        </Link>
      ),
    },
    {
      field: 'sizeText',
      headerName: 'File Size',
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => (
        <IconButton 
          color="error" 
          onClick={() => removeFile(params.row.id)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>
        ),
      },
    ],
    [removeFile]
  );

  const dataGridRows = useMemo(() => 
    uploadedFiles.map((fileObj) => ({
      id: fileObj.id,
      fileName: fileObj.name,
      blobAddress: fileObj.blobAddress,
      sizeText: formatFileSize(fileObj.size),
      file: fileObj.file
    })), [uploadedFiles]
  );
  // Prior Submissions rows - optimized
  const priorSubmissionsRows = useMemo(() => {
    return priorSubmissions.map((row, index) => ({
      ...row,
      id: row.nonPremiumEndorsementId,
      documentName: row.document || '-',
      client: row.client || '-',
      state: row.state || '-',
      fileSizeInKB: row.fileSizeKB || '-',
    }));
  }, [priorSubmissions]);
  const priorSubmissionsColumns: GridColDef[] = useMemo(() => [
    {
      field: 'client',
      headerName: 'Client',
      flex: 2,
      minWidth: 150,
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'documentName',
      headerName: 'Document',
      flex: 3,
      minWidth: 200,
      renderCell: (params) => {
        const { documentName, blobAddress } = params.row;
        
        if (blobAddress && documentName && documentName !== '-') {
          return (
            <Link 
              href={blobAddress} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ 
                textDecoration: 'none', 
                overflow: 'initial', 
                whiteSpace: 'break-spaces',
                lineHeight: 'normal',
                color: '#162E3C',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {documentName}
            </Link>
          );
        }
        return <Typography>{documentName}</Typography>;
      },
    },
    {
      field: 'dateReceived',
      headerName: 'Date Received',
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => {
        const dateReceived = params.row.dateReceived;
        if (!dateReceived) {
          return <Typography>-</Typography>;
        }
        try {
          const date = new Date(dateReceived);
          const formattedDate = date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          });
          return <Typography>{formattedDate}</Typography>;
        } catch (error) {
          return <Typography>-</Typography>;
        }
      },
    },
    {
      field: 'fileSizeInKB',
      headerName: 'File Size (KB)',
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => {
        const fileSizeKB = params.row.fileSizeInKB;
        if (!fileSizeKB || fileSizeKB === '-' || fileSizeKB === '0') {
          return <Typography>-</Typography>;
        }
        return <Typography>{typeof fileSizeKB === 'number' ? `${fileSizeKB.toFixed(2)} KB` : `${fileSizeKB} KB`}</Typography>;
      },
    },
  ], []);

  const selectedStateData = states.find(state => state.stateName === selectedState);
  const selectedPartnerData = partners.find(partner => partner.customerName === selectedPartner);

 

  const handleSendToMISC = async () => {
    
    if (isSubmittingRef.current) {
      return;
    }
    
    if (!selectedState) {
      Swal.fire({
        title: 'Please Select Filing State',
        text: 'You must select a filing state before sending documents to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    // Determine the partner to use - either selected or auto-select if only one
    let partnerToUse = selectedPartner;
    if (!partnerToUse && partners.length === 1) {
      partnerToUse = partners[0].customerName;
      setSelectedPartner(partners[0].customerName); // Update state for UI consistency
    }
    
    if (!partnerToUse) {
      Swal.fire({
        title: 'Please Select Partner',
        text: 'You must select a partner before sending documents to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    if (uploadedFiles.length === 0) {
      Swal.fire({
        title: 'No Documents Uploaded',
        text: 'You must upload at least one document before sending to MISC.',
        icon: 'warning',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      const selectedStateData = states.find(state => state.stateName === selectedState);
      const stateId = selectedStateData?.stateId || 0;
      
      const selectedPartnerData = partners.find(partner => partner.customerName === partnerToUse);
      const partnersId = selectedPartnerData?.partnersId || 0;
      
      formData.append('stateId', stateId.toString());
      formData.append('partnersId', partnersId.toString());
      
      uploadedFiles.forEach((fileObj, index) => {
        formData.append(`file_${index}`, fileObj.file);
        formData.append(`fileName_${index}`, fileObj.name);
        formData.append(`fileSize_${index}`, (fileObj.size / 1024).toFixed(2));
      });

      const response = await apiClient('/NonPremiumEndorsement/Upload', {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
        body: formData
      });

      if (response?.status === true) {
        Swal.fire({
          title: 'Success!',
          text: response.message || 'Files uploaded successfully',
          icon: 'success',
          confirmButtonColor: '#02A4E3',
          customClass: {
            confirmButton: 'btn-radius',
            popup: 'card-radius'
          }
        }).then(() => {
          // Reset form
          setSelectedState('');
          setSelectedPartner('');
          setUploadedFiles([]);
        });
      }
    } catch (error) {
      console.error('Error submitting endorsements:', error);
      Swal.fire({
        title: 'Submission Error',
        text: 'There was an error submitting your documents. Please try again.',
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };


  return (
    <Box sx={{ padding: '10px 10px 0px 10px' }}>
      {/* Welcome Message */}
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          fontSize: '30px',
          fontWeight: 'normal',
          marginBottom: '40px'
        }}
      >
        Welcome <strong>{user?.userFirstName} {user?.userlastName}</strong>!
      </Typography>

      {/* Top Section with Title and Reference Card */}
      <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
        {/* Page Title and Instructions */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 200,
              fontSize: '31px',
              lineHeight: 1.235,
              letterSpacing: '0.00735em',
              color: '#333',
              marginBottom: '8px',
              textAlign: 'left'
            }}
          >
            Non-Premium Endorsements
          </Typography>
           
          {/* Tab Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant={selectedTab === 'new' ? 'contained' : 'outlined'}
              onClick={() => setSelectedTab('new')}
              sx={{
                backgroundColor: selectedTab === 'new' ? '#02A4E3' : 'transparent',
                color: selectedTab === 'new' ? '#fff' : '#666',
                border: selectedTab === 'new' ? 'none' : '1px solid #ccc',
                borderRadius: '8px',
                padding: '10px 20px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: selectedTab === 'new' ? 'bold' : 'normal',
                '&:hover': {
                  backgroundColor: selectedTab === 'new' ? '#0288d1' : '#f5f5f5',
                  border: selectedTab === 'new' ? 'none' : '1px solid #999',
                }
              }}
            >
              + New Submission
            </Button>
            <Button
              variant={selectedTab === 'prior' ? 'contained' : 'outlined'}
              onClick={() => setSelectedTab('prior')}
              sx={{
                backgroundColor: selectedTab === 'prior' ? '#02A4E3' : 'transparent',
                color: selectedTab === 'prior' ? '#fff' : '#666',
                border: selectedTab === 'prior' ? 'none' : '1px solid #ccc',
                borderRadius: '8px',
                padding: '10px 20px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: selectedTab === 'prior' ? 'bold' : 'normal',
                '&:hover': {
                  backgroundColor: selectedTab === 'prior' ? '#0288d1' : '#f5f5f5',
                  border: selectedTab === 'prior' ? 'none' : '1px solid #999',
                }
              }}
            >
              Prior Submissions
            </Button>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, color: '#666', fontSize: '1.1rem', textAlign: 'left', margin: 0, padding: 0 }}>
            {selectedTab === 'new' 
              ? 'Please select the filing state and upload your non-premium endorsements in the space below.'
              : 'View all previously submitted documents below.'}
          </Typography>
        </Box>

        {/* Right Sidebar - Reference Information */}
        <Box sx={{ width: 140 }}>
          <Paper
            sx={{
              p: 2.5,
              textAlign: 'center',
              cursor: 'pointer',
              minHeight: 120,
              height: 140,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
             onClick={() => setShowReferenceSheetDialog(true)}
          >
            <Description sx={{ fontSize: 60, color: '#333', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', lineHeight: 1.2 }}>
              Reference Sheet
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Main Content Area - Full Width */}
      <Box>
        {selectedTab === 'new' ? (
          <>
          {/* Filing State and Partner Selection */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <FormControl fullWidth sx={{ maxWidth: 300 }}>
                <Select
                  value={selectedState}
                  onChange={(event: SelectChangeEvent<string>) => setSelectedState(event.target.value)}
                  label={null}
                  disabled={statesLoading}
                  displayEmpty
                  sx={{ 
                    height: '56px', // Standard Material-UI height
                    '& .MuiSelect-select': {
                      padding: '16px 14px' // Standard padding
                    }
                  }}
                >
                <MenuItem value="">
                  Select State
                </MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.stateId} value={state.stateName}>
                    {state.stateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {  partners.length>1 && (<FormControl fullWidth sx={{ maxWidth: 300 }}>
                <Select
                  value={selectedPartner}
                  onChange={(event: SelectChangeEvent<string>) => setSelectedPartner(event.target.value)}
                  label={null}
                  displayEmpty
                  sx={{ 
                    height: '56px',
                    '& .MuiSelect-select': {
                      padding: '16px 14px' 
                    }
                  }}
                >
                <MenuItem value="">
                  Select Partner
                </MenuItem>
                {partners.map((partner) => (
                  <MenuItem key={partner.partnersId} value={partner.customerName}>
                    {partner.customerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>)}
          </Box>

          {/* File Upload Area */}
          <Paper
            sx={{
              border: isDragOver ? '2px dashed #02A4E3' : '1px dashed #ccc',
              borderRadius: 2,
              padding: 4,
              textAlign: 'center',
              backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              mb: 3,
              minHeight: 200,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
             onDragOver={(event) => {
               event.preventDefault();
               setIsDragOver(true);
             }}
             onDragLeave={(event) => {
               event.preventDefault();
               setIsDragOver(false);
             }}
             onDrop={(event) => {
               event.preventDefault();
               setIsDragOver(false);
               handleFileSelect(event.dataTransfer.files);
             }}
             onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: '#02A4E3', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              Select File or Drag and Drop
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Click to browse files or drag and drop multiple files here
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
               accept={FILE_ACCEPT_ATTRIBUTE}
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </Paper>

        {/* Uploaded Files DataGrid */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
              Attached Documents ({uploadedFiles.length})
            </Typography>
            <Box sx={{ width: '100%', mb: 2 }}>
              <DataGridPro
                autoHeight
                rows={dataGridRows}
                columns={uploadedFilesColumns}
                disableSelectionOnClick
                disableColumnMenu
                pageSize={pageSize}
                pagination
                rowCount={uploadedFiles.length}
                paginationMode="client"
                onPageChange={setCurrentPage}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setCurrentPage(0);
                }}
                rowsPerPageOptions={getRowsPerPageOptions(uploadedFiles.length, pageSize)}
                sx={{
                  borderRadius: '10px',
                  overflow: 'hidden',
                  fontFamily: "'Proxima Nova', sans-serif",
                  fontSize: '16px',
                  fontWeight: 'normal',
                  '& .MuiDataGrid-main': {
                    overflow: 'hidden'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 'normal',
                  },
                  border: '0px'
                }}
              />
            </Box>
          </Box>
        )}

          {/* Send to MISC Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleSendToMISC}
              disabled={loading}
              sx={{
                backgroundColor: '#02A4E3',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#0288d1',
                  color: '#fff'
                },
                '&:disabled': {
                  backgroundColor: '#02A4E3',
                  color: '#fff',
                  opacity: 0.7
                },
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              Send to MISC
            </Button>
          </Box>
          </>
        ) : (
          /* Prior Submissions View */
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
              Prior Submissions ({priorSubmissionsTotalCount || 0})
            </Typography>
            <Box sx={{ width: '100%', height: '400px' }}>
              <DataGridPro
                rows={priorSubmissionsRows}
                columns={priorSubmissionsColumns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                disableColumnMenu
                pageSize={priorSubmissionsPageSize}
                pagination
                rowCount={priorSubmissionsTotalCount}
                paginationMode="server"
                onPageChange={(newPage) => setPriorSubmissionsCurrentPage(newPage + 1)}
                onPageSizeChange={(newPageSize) => {
                  setPriorSubmissionsPageSize(newPageSize);
                  setPriorSubmissionsCurrentPage(1);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  borderRadius: '10px',
                  fontFamily: "'Proxima Nova', sans-serif",
                  fontSize: '16px',
                  fontWeight: 'normal',
                  '& .MuiDataGrid-main': {
                    overflow: 'auto'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 'normal',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto'
                  },
                  border: '0px'
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="h6" sx={{ color: '#666' }}>
                        No prior submissions found.
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Important Note - Only show for New Submission tab */}
      {selectedTab === 'new' && (
        <Typography variant="h6" sx={{ 
          backgroundColor: '#ffff65', 
          padding: '8px', 
          borderRadius: '4px',
          fontWeight: '500',
          textAlign: 'center'
        }}>
          <strong>🚨 NOTE :</strong> Please DO NOT upload $0 New and Renewal Business policies on this screen. 
          Contact{' '}
          <Link href="mailto:MISCFilings@morrisisc.com" color="primary">
            MISCFilings@morrisisc.com
          </Link>{' '}
          for further assistance as there may be additional filing requirements & procedures based on the home state.
        </Typography>
      )}
      <Dialog
        open={showReferenceSheetDialog}
        onClose={() => setShowReferenceSheetDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Non-Premium Endorsement Requirements
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'normal', mt: 1 }}>
              <strong>Reference Sheet</strong>
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <img 
              src="/assets/brand/MISC_Full_Type_Lockup_Secondary.png" 
              alt="MISC Morris Insurance Services & Consulting" 
              style={{ 
                height: '50px', 
                width: 'auto',
                objectFit: 'contain'
              }} 
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto', padding: 2 }}>
            {/* CALIFORNIA Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                CALIFORNIA
              </Typography>
              
              {/* Nil Endorsements Content */}
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="h6" sx={{color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                  Nil Endorsements
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 2 ,textAlign:'left',fontSize:'18px'}}>
                   Although the SLA accepts all nil endorsements for review, the following nil endorsements must be submitted to us to update our database:
                </Typography>
                
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy number change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Insured name change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Insured address change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy effective date change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Policy expiration date change*
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Change of insurer
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    Change of coverage
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666',textAlign:'left' }}>
                  *Endorsements that extend the original policy period are not considered part of the nil endorsement process and should follow the same filing requirements as a renewal.
                </Typography>
              </Box>
              <Link href="https://www.slacal.com/docs/default-source/general-content-documents/tutorials/nil-endorsements.pdf" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3'}}>
              Filing Requirements with the Surplus Line Association (slacal.com)
              </Link>
            </Box>

            {/* NEVADA Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                NEVADA
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold',textAlign:'left' }}>
                What transaction types am I required to file?
              </Typography>
              <Typography variant="body1" sx={{ mb: 2,  textAlign:'justify' }}>
                <strong>Answer:</strong> NAC 685A.240 A filing must be made for a new or a renewal of a contract of insurance. If the coverage and category code does not appear on the approved export list, the filing must include declination details. You are also required to make an amended filing for each cancellation and <strong> each endorsement which changes the named insured, the description or location of the subject of insurance, the coverage, conditions, terms, premiums, or participating insurers.</strong> Endorsements extending the policy for any length of time are filed as extensions and treated the same as renewals.
              </Typography>
              <Link href="https://www.nsla.org/Resources/FAQs" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.nsla.org/Resources/FAQs
              </Link>
            </Box>

            {/* PENNSYLVANIA Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                PENNSYLVANIA
              </Typography>
              <Typography  sx={{ mb: 2 ,textAlign:'left' }}>
                A New Endorsement should be submitted when any one of the following changes to a policy (the original placement) has occurred:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change in Policy Premium (additional or returned)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change in <strong>Named Insured</strong>
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change to the <strong>Location of Risk</strong> (for placements with multiple locations in Pennsylvania, the change endorsement should indicate if the location with the most exposure has been changed)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change or revision to the <strong>Policy Number</strong> (other than a policy rewrite)
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  A change to the <strong>Policy Period</strong>
                </Typography>
              </Box>
              <Link href="https://www.pasla.org/Documents/ProcedureManual.pdf" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.pasla.org/Documents/ProcedureManual.pdf
              </Link>
            </Box>

            {/* TEXAS Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                TEXAS
              </Typography>
              
              {/* Non-Premium Transactions Q&A Content */}
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="h6" sx={{ color: '#FF8C00', fontWeight: 'bold', mb: 2 }}>
                  Q. Are we required to file non-premium transactions?
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                  A. Yes; you must file the following non-premium transactions:
                </Typography>
                
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    policy and or binder
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    cancellations and reinstatements
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    policy number change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    insured name change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    security or insurer change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    inception date change
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                    expiration date change
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                  You may find instructions on how to do these transactions at{' '}
                  <Link href="https://www.sltx.org/education/training-videos/#" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
                    https://www.sltx.org/education/training-videos/#
                  </Link>
                </Typography>
              </Box>
              
              <Link href="https://www.sltx.org/help/faq/online-filer-faqs/" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.sltx.org/help/faq/online-filer-faqs/
              </Link>
            </Box>

            {/* WASHINGTON Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#02A4E3', fontWeight: 'bold', mb: 2 }}>
                WASHINGTON
              </Typography>
              <Typography variant="body1" sx={{ mb: 2,textAlign:'left' }}>
                (7) Endorsements or other modifications of coverages previously submitted must be promptly filed with the Association where the endorsement or other modification involves any additional or return premium, changes in security, or a change of policy number.
              </Typography>
              <Box component="ol" type='a' sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  Endorsements having additional or return premiums must include details, e.g. premiums, state tax, stamping fee.
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  <strong>
                  Non-money endorsements need not be filed, except for changes of security or policy number.
                  </strong>
                </Typography>
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  Certifications should not be submitted with an endorsement.
                </Typography>
              </Box>
              <Link href="https://www.surpluslines.org/filing-information/" target="_blank" rel="noopener noreferrer" sx={{ color: '#02A4E3' }}>
              https://www.surpluslines.org/filing-information/
              </Link>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReferenceSheetDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NonPremiumEndorsements;
